import React from "react";
import type { AccentTokenKey } from "pages/settings/components/settingsTheme";

interface EmptyStateProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  hint?: string;
  accent?: AccentTokenKey;
  className?: string;
}

const ringMap: Record<AccentTokenKey, { ring: string; icon: string; border: string }> = {
  indigo: {
    ring: "from-hue-indigo/10 to-hue-indigo/15",
    icon: "text-hue-indigo",
    border: "border-hue-indigo/20",
  },
  violet: {
    ring: "from-hue-violet/10 to-hue-violet/15",
    icon: "text-hue-violet",
    border: "border-hue-violet/20",
  },
  emerald: {
    ring: "from-hue-emerald/10 to-hue-emerald/15",
    icon: "text-hue-emerald",
    border: "border-hue-emerald/20",
  },
  cyan: {
    ring: "from-hue-cyan/10 to-hue-cyan/15",
    icon: "text-hue-cyan",
    border: "border-hue-cyan/20",
  },
  teal: {
    ring: "from-hue-teal/10 to-hue-teal/15",
    icon: "text-hue-teal",
    border: "border-hue-teal/20",
  },
  blue: {
    ring: "from-hue-blue/10 to-hue-blue/15",
    icon: "text-hue-blue",
    border: "border-hue-blue/20",
  },
  red: { ring: "from-danger/10 to-danger/15", icon: "text-danger", border: "border-danger/20" },
  gray: {
    ring: "from-foreground/5 to-foreground/10",
    icon: "text-muted-foreground",
    border: "border-border",
  },
  brand: {
    ring: "from-primary/10 to-secondary/10",
    icon: "text-primary",
    border: "border-primary/20",
  },
};

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  hint,
  accent = "indigo",
  className = "",
}) => {
  const colors = ringMap[accent];
  return (
    <div className={`text-center py-6 ${className}`}>
      <div
        className={`w-16 h-16 bg-gradient-to-br ${colors.ring} rounded-full flex items-center justify-center mx-auto mb-4 border ${colors.border}`}
      >
        <div className={`${colors.icon} text-xl`}>{icon}</div>
      </div>

      <h3 className="text-foreground font-medium text-lg mb-2">{title}</h3>
      {description && (
        <p className="text-muted-foreground text-sm mb-5 max-w-sm mx-auto">{description}</p>
      )}

      {action}

      {hint && <p className="text-muted-foreground/50 text-xs mt-3">{hint}</p>}
    </div>
  );
};

export default EmptyState;
